// 案 D: 忠実モバイル化
// - 既存 PC 版の主要要素 (KpiCards 9指標 / アラート / 月別表) を responsive で縦積み
// - 学習コストゼロ、 PC 版を縮小したイメージ
// - 表は横スクロール許容

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useMeta, TOP9_METRICS, ml, ALERT_CATEGORIES } from "../lib/mobileApi";
import { MobileShell } from "../components/MobileShell";
import { StatPill, sentimentFromRatio, sentimentFromDelta } from "../components/StatBadge";
import { fmtByUnitMain, fmtByUnit, fmtRatio } from "../../lib/format";
import type { KpiResponse, AlertsResponse, AlertItem, MetricRow, Direction, MonthlyResponse } from "@shared/api";

type Tab = "実績" | "アラート" | "推移";

export function PrototypeD() {
  const { data: meta } = useMeta();
  const [tab, setTab] = useState<Tab>("実績");
  const [focusedDept, setFocusedDept] = useState<string | null>(null);

  if (!meta) return <div className="p-6 text-sm text-slate-400">読み込み中…</div>;
  const asOfMonth = meta.dateMax;
  const periodFrom = monthShift(asOfMonth, -11);

  return (
    <MobileShell
      title={focusedDept ? `🎯 ${focusedDept}` : "病院全体 (PC 版縮小)"}
      prototypeId="D"
      prototypeName="忠実モバイル化"
      asOfMonth={asOfMonth}
      bottomNav={
        <div className="grid grid-cols-3 gap-1">
          {(["実績", "アラート", "推移"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`text-xs py-1.5 rounded font-medium ${
                tab === t ? "bg-slate-900 text-white" : "text-slate-600 active:bg-slate-100"
              }`}
            >{t}</button>
          ))}
        </div>
      }
    >
      {focusedDept && (
        <button
          onClick={() => setFocusedDept(null)}
          className="w-full bg-amber-100 text-amber-800 text-xs font-medium py-1.5 rounded border border-amber-200"
        >× フォーカス解除</button>
      )}
      {tab === "実績" && (
        <KpiGridSection metrics={meta.metrics} asOfMonth={asOfMonth} periodFrom={periodFrom} focusedDept={focusedDept} />
      )}
      {tab === "アラート" && (
        <AlertsSection asOfMonth={asOfMonth} onSelectDept={setFocusedDept} focusedDept={focusedDept} />
      )}
      {tab === "推移" && (
        <MonthlyTableSection metric="入院収入" asOfMonth={asOfMonth} periodFrom={periodFrom} />
      )}
    </MobileShell>
  );
}

function KpiGridSection({ metrics, asOfMonth, periodFrom, focusedDept }: {
  metrics: MetricRow[]; asOfMonth: string; periodFrom: string; focusedDept: string | null;
}) {
  return (
    <>
      <div className="text-[10px] text-slate-500">主要 9 指標 (PC 版と同じ並び)</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 gap-2">
        {TOP9_METRICS.map((m) => (
          <KpiCardLite key={m} metricName={m} metrics={metrics} asOfMonth={asOfMonth} periodFrom={periodFrom} focusedDept={focusedDept} />
        ))}
      </div>
    </>
  );
}

function KpiCardLite({ metricName, metrics, asOfMonth, periodFrom, focusedDept }: {
  metricName: string; metrics: MetricRow[]; asOfMonth: string; periodFrom: string; focusedDept: string | null;
}) {
  const metricDef = metrics.find((x) => x["集計区分"] === metricName);
  const unit = metricDef?.unit ?? "";
  const direction = (metricDef?.direction ?? "neutral") as Direction;
  const { data } = useQuery<KpiResponse>({
    queryKey: ["kpi-d", metricName, asOfMonth, focusedDept],
    queryFn: () => api.kpi({
      granularity: "小", iryo: "合計", yotei: "合計", metric: metricName, asOfMonth, periodFrom,
      focusedDept: focusedDept ?? undefined,
    }),
    placeholderData: (prev) => prev,
  });
  const adj = (v: number | null | undefined): number | null => {
    if (v == null) return null;
    if (["GP", "GP単価"].includes(metricName) && v === 0) return null;
    return v;
  };
  const current = adj(data?.current);
  const main = fmtByUnitMain(current, unit);
  const ratio = data?.budget != null && current != null && data.budget !== 0 ? current / data.budget : null;
  const sent = sentimentFromRatio(ratio, direction);
  const yoyDelta = current != null && data?.prevYear != null && data.prevYear !== 0
    ? (current - data.prevYear) / Math.abs(data.prevYear) : null;
  const yoySent = sentimentFromDelta(yoyDelta, direction);

  return (
    <div className="bg-white border border-slate-200 rounded p-2">
      <div className="text-[10px] text-slate-500 truncate">{ml(metricName)} <span className="text-slate-400">({unit})</span></div>
      <div className="text-base font-bold text-slate-900 tabular-nums leading-tight">{main.value}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">予算 {fmtByUnit(data?.budget, unit)}</div>
      <div className="mt-1 flex gap-1 flex-wrap">
        <StatPill label="予" value={ratio != null ? fmtRatio(ratio) : "—"} sentiment={sent} size="xs" />
        <StatPill label="年" value={yoyDelta != null ? `${yoyDelta >= 0 ? "+" : ""}${(yoyDelta * 100).toFixed(1)}%` : "—"} sentiment={yoySent} size="xs" />
      </div>
    </div>
  );
}

function AlertsSection({ asOfMonth, onSelectDept, focusedDept }: {
  asOfMonth: string; onSelectDept: (d: string) => void; focusedDept: string | null;
}) {
  const { data } = useQuery<AlertsResponse>({
    queryKey: ["alerts-d", asOfMonth],
    queryFn: () => api.alerts({ granularity: "小", iryo: "合計", yotei: "合計", asOfMonth }),
    placeholderData: (prev) => prev,
  });
  return (
    <div className="space-y-3">
      {ALERT_CATEGORIES.map((c) => {
        const items = (data?.[c.key] as AlertItem[] | undefined) ?? [];
        return (
          <section key={c.key} className="bg-white border border-slate-200 rounded p-2">
            <div className="flex items-center gap-1.5 mb-1.5">
              <span className="text-base">{c.icon}</span>
              <h3 className="text-xs font-semibold text-slate-700">{c.label}</h3>
              <span className="text-[10px] text-slate-400 ml-auto">{ml(c.metric)}</span>
            </div>
            {items.length === 0 ? (
              <div className="text-[10px] text-slate-400">該当なし</div>
            ) : (
              <ol className="space-y-1">
                {items.slice(0, 6).map((it, i) => (
                  <li key={`${c.key}-${i}`}>
                    <button
                      onClick={() => onSelectDept(it["診療科"])}
                      className={`w-full flex items-center justify-between text-xs px-1.5 py-1 rounded ${
                        focusedDept === it["診療科"] ? "bg-amber-50 ring-1 ring-amber-300" : "active:bg-slate-100"
                      }`}
                    >
                      <span className="text-slate-700 truncate flex-1 text-left">
                        <span className="text-[9px] font-mono text-slate-400 mr-1">#{i + 1}</span>
                        {it["診療科"]}
                      </span>
                      <span className="text-red-700 font-semibold tabular-nums">
                        {it.ratio != null ? fmtRatio(it.ratio) : "—"}
                      </span>
                    </button>
                  </li>
                ))}
              </ol>
            )}
          </section>
        );
      })}
    </div>
  );
}

function MonthlyTableSection({ metric, asOfMonth, periodFrom }: {
  metric: string; asOfMonth: string; periodFrom: string;
}) {
  const { data, isLoading } = useQuery<MonthlyResponse>({
    queryKey: ["monthly-d", metric, asOfMonth],
    queryFn: () => api.monthly({
      iryo: "合計", yotei: "合計", metric,
      dateFrom: periodFrom, dateTo: asOfMonth,
    }),
    placeholderData: (prev) => prev,
  });
  if (isLoading || !data) return <div className="text-xs text-slate-400">読み込み中…</div>;

  // 病院全体行を date 別にピボット
  const hospital = data.rows.filter((r) => r["診療科"] === "総合計").sort((a, b) => a.date.localeCompare(b.date));
  if (hospital.length === 0) return <div className="text-xs text-slate-500">データなし</div>;

  return (
    <div>
      <div className="text-[10px] text-slate-500 mb-1">{ml(metric)} 月別推移 (病院全体)</div>
      <div className="overflow-x-auto -mx-3">
        <table className="text-[11px] w-full">
          <thead className="bg-slate-100 sticky top-0">
            <tr>
              <th className="text-left px-2 py-1 font-semibold text-slate-600">月</th>
              <th className="text-right px-2 py-1 font-semibold text-slate-600">実績</th>
              <th className="text-right px-2 py-1 font-semibold text-slate-600">予算</th>
              <th className="text-right px-2 py-1 font-semibold text-slate-600">予算比</th>
            </tr>
          </thead>
          <tbody>
            {hospital.map((r) => {
              const ratio = r.予算 != null && r.実績 != null && r.予算 !== 0 ? r.実績 / r.予算 : null;
              const sent = sentimentFromRatio(ratio, "higher_is_better");
              return (
                <tr key={r.date} className="border-b border-slate-100">
                  <td className="px-2 py-1 text-slate-700">{r.date.slice(0, 7)}</td>
                  <td className="px-2 py-1 text-right text-slate-700 tabular-nums">{fmtByUnit(r.実績, "千円")}</td>
                  <td className="px-2 py-1 text-right text-slate-500 tabular-nums">{fmtByUnit(r.予算, "千円")}</td>
                  <td className="px-2 py-1 text-right">
                    <StatPill value={ratio != null ? fmtRatio(ratio) : "—"} sentiment={sent} size="xs" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function monthShift(d: string, delta: number): string {
  const [y, m, day] = d.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + delta, day));
  return [dt.getUTCFullYear(), String(dt.getUTCMonth() + 1).padStart(2, "0"), String(dt.getUTCDate()).padStart(2, "0")].join("-");
}
